import { useEffect, useState, useLayoutEffect } from "react";
import { useSelector } from "react-redux";
import { ProfileStandardClaims } from 'oidc-client';
import { addMessageListener, IframeComponent } from '@hcecbp/uiintegrationcomponent';
import { useAuthService } from "../../../auth/hooks/useAuthService";
import { authService } from "../../../auth/services/authService";
import { IOrganisationState } from "../../commonUtils/Redux/Global/globalShared";
import './index.scss';
import { useFlags } from "launchdarkly-react-client-sdk";
import { getFeatureFlagValuebasedOnSIOrg, getFeatureFlagValuebasedOnSIOrgWhitelist } from '../../commonUtils/LDFlagUtils';
import { useHistory } from "react-router-dom";
import i18next from "i18next";
import { ContentLoader } from "@forge/common";
import useAuthData from "../../../customHooks/useAuthData";

const LandingPageDashboard = () => {
    const { globalPermissions, loadGlobalPermissions, user } = useAuthService();
    const orgSelected = useSelector((state: IOrganisationState) => state.globalShared.gatewaySelectedOrganisation);
    const userList = useSelector((state: IOrganisationState) => state.globalShared.userList);
    const organisations = useSelector(
    (state: IOrganisationState) => state.globalShared.organisationList
    );
    const environmentVariables = (window as any).ENV;
    const USER_LOGGEDIN = "oidcUserLoggedIn";
    const USER_LIST = "userList";
    const LANDING_PAGE_DASHBOARD_HOST_URL = environmentVariables.LANDING_PAGE_DASHBOARD_HOST_URL;
    const getTheme = () => localStorage.getItem('COLOR_THEME') || '0';
    const getLocale = () => i18next.language || 'en-US';
    const getIframeElement = () => document.getElementById('landing-page-dashboard-iframe-id') as HTMLIFrameElement;
    const getWindowInnerHeight = () => window.innerHeight;
    const getContentHeight = (inputInnerHeight?: number) => (inputInnerHeight || getWindowInnerHeight()) - 50;
    const [isIFrameLoaded, setIsIFrameLoaded] = useState<boolean>(false);
    const [iframeHeight, setIframeHeight] = useState<number>(getContentHeight());
    const [loading, setLoading] = useState(true);
    const featureFlags = useFlags();
    const { profile } = useAuthData();

    const history = useHistory();

    const getAccessToken = async () => {
        try {
            const token = await authService.getAccessToken();
            return token;
        } catch (error) {
            console.error('[LandingPageDashboard] Error getting access token:', error);
            throw error;
        }
    };

    const getAuthContextData = async () => {
        const rbmAccessToken = await getAccessToken();
        const profileData = profile || {};
        return { rbmAccessToken, profileData };
    };
    
    const featureFlagsProps = {
      ...featureFlags,
      isDistributorFlowEnabled: getFeatureFlagValuebasedOnSIOrg(
        'enableBACSFlowDistributor',
        featureFlags,
        orgSelected,
        organisations
      ),
      enableARRLicenseManagement: getFeatureFlagValuebasedOnSIOrgWhitelist(
        'enableArrLicenseFlowForSi',
        featureFlags,
        orgSelected,
        organisations
      ),
      enableARRLicenseOverviewAndDownloadForSI: authService.isLicensePrivilegedUser,
    };

    useLayoutEffect(() => {
        const handleResize = () => setIframeHeight(getContentHeight(getWindowInnerHeight()));
        window.addEventListener('resize', handleResize);
        handleResize();

        return () => {
            window.removeEventListener('resize', handleResize);
        };
    }, []);

    useEffect(() => {
        if (orgSelected?.id && orgSelected?.name) {
            setLoading(true);
            const timer = setTimeout(() => {
                setLoading(false);
            }, 1000);
            return () => clearTimeout(timer);
        }
    }, [orgSelected]);

    useEffect(() => {
        if (user && globalPermissions.length === 0) {
        loadGlobalPermissions().catch((error) => {
            console.error('LandingPageDashboard Failed to load global permissions:', error);
        });
        }
    }, [user, globalPermissions.length, loadGlobalPermissions]);

    useEffect(() => {
        const event = addMessageListener(LANDING_PAGE_DASHBOARD_HOST_URL, (message) => {
            if (message?.type === 'appContext') {
                const receivedVal = JSON.parse(message?.payload?.viewType || '{}');
                const viewPath = message?.payload?.viewPath || '';
                if (receivedVal?.iotLoaded) {
                    setIsIFrameLoaded(true);
                }
                if(viewPath === 'orgDashboard'){
                    history.push("/app/feature/organisations")
                }
                if(viewPath === 'gatewayManagement'){
                    history.push("/app/feature/gateway-management")
                }
                if(viewPath === 'subscriptionDashboard'){
                    history.push(`/app/feature/subscription?subs-status=${receivedVal?.subscriptionStatus || ''}`)
                }
                if(viewPath === 'licenseManagement'){
                    history.push("/app/feature/licenses")
                }
                if(viewPath === 'orgDashboardLicenseTab'){
                    const customerId = receivedVal?.customerId || '';
                    const customerName = receivedVal?.customerName || '';
                    history.push(`/app/feature/organisations?customerId=${customerId}&orgName=${customerName}`)
                }
                if(viewPath === 'licenseManagementFilter'){
                    history.push(`/app/feature/licenses?license-filter=${receivedVal?.licenseFilter || ''}`)
                }
            }
        });

        return () => {
            if (event) {
                event.dispose();
            }
        };
    }, []);

    const sendViewTypeToIframe = async () => {
        const iframe = getIframeElement();
        const iframeContentWindow = iframe?.contentWindow;
        if (iframeContentWindow && orgSelected?.id) {
            try {
                // Get fresh token and matching profile snapshot in one call path
                const { rbmAccessToken, profileData } = await getAuthContextData();
                
                const viewType = {
                    theme: getTheme(),
                    locale: getLocale(),
                    rbmAccessToken,
                    profileData,
                    selectedOrg: orgSelected?.id || '',
                    selectedOrgName: orgSelected?.name,
                    featureFlags: featureFlagsProps,
                    globalPermissions: globalPermissions,
                    selectedSIOrg: orgSelected || '',
                };
                updateStorage(profileData);
                const targetOrigin = iframe?.src
                    ? new URL(iframe.src).origin
                    : LANDING_PAGE_DASHBOARD_HOST_URL;
                iframeContentWindow.postMessage({
                    type: 'appContext',
                    payload: {
                        viewType: JSON.stringify(viewType)
                    }
                }, targetOrigin);
            } catch (error) {
                console.error('[LandingPageDashboard] Error sending view type to iframe:', error);
            }
        }
    };

    useEffect(() => {
        if (orgSelected && isIFrameLoaded) {
            sendViewTypeToIframe();
        }
    }, [
        orgSelected,
        isIFrameLoaded,
        i18next.language,
        localStorage.getItem('COLOR_THEME')
    ]);

    const updateStorage = (profileData: ProfileStandardClaims | undefined) => {
        sessionStorage.setItem(USER_LOGGEDIN, profileData?.given_name || "");
        sessionStorage.setItem(USER_LIST, JSON.stringify(userList));
    }

    return (
        <div>
            {/* IFrame Component */}
            {!isIFrameLoaded && <div className="gateway-loader-cls"><ContentLoader /></div>}
            <div style={{ display: !isIFrameLoaded ? "none" : "block" }}>
                <IframeComponent
                    width="100%"
                    height={`${iframeHeight}px`}
                    id="landing-page-dashboard-iframe-id"
                    url={`${LANDING_PAGE_DASHBOARD_HOST_URL}/landing-page`}
                />
            </div>
        </div>
    );
};

export default LandingPageDashboard;